import { useId, useLayoutEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input, Toggle, useModalFooterSlot } from '@/components/ui'
import type { NotificationTemplate, NotificationTemplateUpdatePayload } from '@/types'
import { useUpdateNotificationTemplate } from '../hooks/useNotifications'

const editTemplateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  inAppEnabled: z.boolean(),
  inAppTitle: z.string().min(1, 'In-app title is required'),
  inAppMessage: z.string().min(1, 'In-app message is required'),
  emailEnabled: z.boolean(),
  emailSubject: z.string().optional(),
  emailBody: z.string().optional(),
  isActive: z.boolean(),
})

type EditTemplateFormValues = z.infer<typeof editTemplateSchema>

function formValuesToPayload(values: EditTemplateFormValues): NotificationTemplateUpdatePayload {
  return {
    name: values.name.trim(),
    inApp: {
      enabled: values.inAppEnabled,
      title: values.inAppTitle.trim(),
      message: values.inAppMessage.trim(),
    },
    email: {
      enabled: values.emailEnabled,
      subject: values.emailSubject?.trim(),
      body: values.emailBody?.trim(),
    },
    isActive: values.isActive,
  }
}

export interface EditNotificationTemplateFormProps {
  template: NotificationTemplate | null
  onSuccess: () => void
  onCancel?: () => void
}

export function EditNotificationTemplateForm({
  template,
  onSuccess,
  onCancel,
}: EditNotificationTemplateFormProps): React.ReactElement {
  const formId = useId()
  const setModalFooter = useModalFooterSlot()
  const updateTemplate = useUpdateNotificationTemplate()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EditTemplateFormValues>({
    resolver: zodResolver(editTemplateSchema),
    values: template
      ? {
          name: template.name,
          inAppEnabled: template.inApp.enabled,
          inAppTitle: template.inApp.title,
          inAppMessage: template.inApp.message,
          emailEnabled: template.email.enabled,
          emailSubject: template.email.subject ?? '',
          emailBody: template.email.body ?? '',
          isActive: template.isActive,
        }
      : undefined,
    defaultValues: {
      name: '',
      inAppEnabled: true,
      inAppTitle: '',
      inAppMessage: '',
      emailEnabled: false,
      emailSubject: '',
      emailBody: '',
      isActive: true,
    },
  })

  const inAppEnabled = watch('inAppEnabled')
  const emailEnabled = watch('emailEnabled')

  const onSubmit = (values: EditTemplateFormValues): void => {
    if (!template) return
    const payload = formValuesToPayload(values)
    updateTemplate.mutate(
      { id: template._id, payload },
      {
        onSuccess: () => {
          reset()
          onSuccess()
        },
      }
    )
  }

  const isPending = updateTemplate.isPending || isSubmitting
  const readOnly = template?.isSystemTemplate ?? false

  useLayoutEffect(() => {
    if (!setModalFooter) return
    if (!template) {
      setModalFooter(null)
      return
    }
    if (readOnly) {
      setModalFooter(
        onCancel != null ? (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Close
          </Button>
        ) : null
      )
    } else {
      setModalFooter(
        <>
          {onCancel != null && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button form={formId} type="submit" loading={isPending}>
            <span className={isPending ? 'ml-2' : ''}>Save changes</span>
          </Button>
        </>
      )
    }
    return () => setModalFooter(null)
  }, [setModalFooter, template, readOnly, onCancel, isPending, formId])

  if (!template) return <></>

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Template name"
        {...register('name')}
        error={errors.name?.message}
        disabled={readOnly}
      />
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">In-app</h3>
        <Toggle
          label="Enable in-app notification"
          checked={inAppEnabled}
          onChange={(v) => setValue('inAppEnabled', v)}
          disabled={readOnly}
        />
        <Input
          label="Title"
          {...register('inAppTitle')}
          error={errors.inAppTitle?.message}
          disabled={readOnly}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Message
          </label>
          <textarea
            {...register('inAppMessage')}
            rows={3}
            disabled={readOnly}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          {errors.inAppMessage && (
            <p className="mt-1 text-sm text-danger">{errors.inAppMessage.message}</p>
          )}
        </div>
      </div>
      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Email</h3>
        <Toggle
          label="Enable email"
          checked={emailEnabled}
          onChange={(v) => setValue('emailEnabled', v)}
          disabled={readOnly}
        />
        <Input
          label="Subject"
          {...register('emailSubject')}
          disabled={readOnly}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Body
          </label>
          <textarea
            {...register('emailBody')}
            rows={4}
            disabled={readOnly}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      </div>
      <Toggle
        label="Template active"
        checked={watch('isActive')}
        onChange={(v) => setValue('isActive', v)}
        disabled={readOnly}
      />
      {!setModalFooter && !readOnly && (
        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" loading={isPending}>
            <span className={isPending ? 'ml-2' : ''}>Save changes</span>
          </Button>
        </div>
      )}
      {readOnly && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          System templates cannot be edited.
        </p>
      )}
    </form>
  )
}

EditNotificationTemplateForm.displayName = 'EditNotificationTemplateForm'
